/**
 * The answer key of a minesweeper puzzle is the mine layout. Kept apart from the form
 * component so the eagerly loaded module file never pulls the author's board into the player
 * bundle.
 */
import { blankRows, resizeRows } from "./progress";
import { EMPTY, FLAG, type MinesweeperDefinition, type MinesweeperSolution } from "./types";

/**
 * What the core hands the form when nothing is queued: no rows at all, so the form can tell
 * "nothing typed" from a deliberately empty field. `minesOf` pads it to a blank grid.
 */
export function emptySolution(_definition: MinesweeperDefinition): MinesweeperSolution {
  return { rows: [] };
}

/** Whether a stored value is a mine layout at all, whatever its size. */
export function isMineGrid(value: unknown): value is MinesweeperSolution {
  if (typeof value !== "object" || value === null || Array.isArray(value)) return false;
  const rows = (value as { rows?: unknown }).rows;
  return Array.isArray(rows) && rows.every((row) => typeof row === "string" && /^[*.]*$/.test(row));
}

/** Nothing queued: not a mine layout, or the empty one the core hands out. */
export function isAbsent(value: unknown): boolean {
  return !isMineGrid(value) || value.rows.length === 0;
}

/** Reads whatever is stored, however wrong, as a mine layout of the definition's size. */
export function minesOf(value: unknown, definition: MinesweeperDefinition): string[] {
  if (!isMineGrid(value)) return blankRows(definition.rows, definition.cols);
  return resizeRows(
    value.rows.map((row) => Array.from(row, (c) => (c === FLAG ? FLAG : EMPTY)).join("")),
    definition.rows,
    definition.cols,
  );
}
